<template>
  <footer class="df-footer bg-[#1A1A1A] text-white">
    <!-- ========== H5 / 平板：手风琴 ========== -->
    <div v-if="!$isDesktop" class="df-footer-h5 px-4 sm:px-6 pt-2 pb-8">
      <div class="df-footer-accordion">
        <div v-for="(col, cIndex) in linkColumns" :key="cIndex" class="df-footer-acc-item border-b border-white/15">
          <button type="button" class="df-footer-acc-trigger w-full flex items-center justify-between py-4 text-left"
            :aria-expanded="isAccOpen(cIndex)" @click="toggleAcc(cIndex)">
            <span class="text-sm font-medium text-white/90">{{ $t(col.title) }}</span>
            <i class="el-icon-arrow-up text-white/60 text-xs transition-transform duration-200"
              :class="{ 'rotate-180': !isAccOpen(cIndex) }"></i>
          </button>
          <div v-show="isAccOpen(cIndex)" class="df-footer-acc-panel pb-4 pl-0">
            <ul class="space-y-3">
              <li v-for="(link, lIndex) in col.links" :key="lIndex">
                <router-link v-if="link.path" :to="link.path"
                  class="df-footer-link text-xs text-white/60 active:text-white/90">
                  {{ formatLinkName(col, link) }}
                </router-link>
                <a v-else-if="link.href" :href="link.href" target="_blank" rel="noopener noreferrer"
                  class="df-footer-link text-xs text-white/60 active:text-white/90">
                  {{ formatLinkName(col, link) }}
                </a>
                <button v-else type="button" class="df-footer-link text-xs text-white/60 active:text-white/90 text-left"
                  @click="onLinkClick(link)">
                  {{ formatLinkName(col, link) }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 社交图标：居中，竖线分隔 -->
      <div class="flex items-center justify-center mt-8 mb-6">
        <template v-for="(item, index) in socialList">
          <a v-if="item.link" :key="item.name" :href="item.link" target="_blank" rel="noopener noreferrer" :aria-label="item.name"
            class="w-8 h-8 flex items-center justify-center mx-4">
            <img :src="item.iconWhite" :alt="item.name" class="w-8 h-8 object-contain">
          </a>
          <span v-else-if="item.isWechat" :key="item.name"
            class="df-wechat-trigger w-8 h-8 flex items-center justify-center mx-4" :aria-label="item.name">
            <img :src="item.iconWhite" :alt="item.name" class="w-8 h-8 object-contain">
            <span v-if="item.qrcode" class="df-wechat-popup" role="tooltip">
              <img :src="item.qrcode" :alt="$t('微信二维码')" class="df-wechat-qrcode">
            </span>
          </span>
          <span v-else :key="item.name" class="w-8 h-8 flex items-center justify-center mx-4" :aria-label="item.name">
            <img :src="item.iconWhite" :alt="item.name" class="w-8 h-8 object-contain">
          </span>
          <span v-if="index < socialList.length - 1" :key="'sep-' + index" class="w-px h-3.5 bg-white/25"></span>
        </template>
      </div>

      <!-- 底部信息 -->
      <div class="border-t border-white/15 pt-5 text-center text-[11px] text-white/45 leading-relaxed space-y-1.5">
        <div class="flex flex-wrap items-center justify-center gap-x-1.5">
          <a v-if="serviceMobile" :href="`tel:${serviceMobile}`" class="text-white/45">{{ $t('客服电话：{phone}', { phone: serviceMobile }) }}</a>
          <span class="text-white/25">|</span>
          <button type="button" class="df-footer-link text-white/45" @click="onPage('13')">{{ $t('隐私政策') }}</button>
          <span class="text-white/25">|</span>
          <button type="button" class="df-footer-link text-white/45" @click="onPage('14')">{{ $t('用户协议') }}</button>
        </div>
        <div>Copyright © 2025 CHAOS科技有限公司</div>
        <div v-if="siteBeian">{{ siteBeian }}</div>
      </div>
    </div>

    <!-- ========== PC ≥1024：六列 ========== -->
    <div v-else class="container-custom py-12 md:py-14 lg:py-16 xl:py-20">
      <div class="df-footer-columns grid grid-cols-6 gap-x-6 xl:gap-x-10">
        <div v-for="(col, cIndex) in linkColumns" :key="cIndex" class="min-w-0">
          <h3 class="text-sm md:text-base font-medium mb-4 md:mb-5 lg:mb-6">
            {{ $t(col.title) }}
          </h3>
          <ul class="space-y-2.5 md:space-y-3">
            <li v-for="(link, lIndex) in col.links" :key="lIndex">
              <router-link v-if="link.path" :to="link.path"
                class="df-footer-link text-xs md:text-sm text-white/75 hover:text-white transition-colors">
                {{ formatLinkName(col, link) }}
              </router-link>
              <a v-else-if="link.href" :href="link.href" target="_blank" rel="noopener noreferrer"
                class="df-footer-link text-xs md:text-sm text-white/75 hover:text-white transition-colors">
                {{ formatLinkName(col, link) }}
              </a>
              <button v-else type="button"
                class="df-footer-link text-xs md:text-sm text-white/75 hover:text-white transition-colors text-left"
                @click="onLinkClick(link)">
                {{ formatLinkName(col, link) }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="df-footer-meta mt-12 md:mt-14 lg:mt-16 flex items-center justify-between gap-8">
        <div class="flex items-center gap-8 lg:gap-16 xl:gap-24">
          <a v-if="serviceMobile" :href="`tel:${serviceMobile}`"
            class="text-xs md:text-sm text-white/80 hover:text-white transition-colors whitespace-nowrap">
            {{ $t('客服电话：{phone}', { phone: serviceMobile }) }}
          </a>
          <div class="flex items-center gap-5 md:gap-6 lg:gap-8">
            <button type="button" class="text-xs md:text-sm text-white/75 hover:text-white transition-colors"
              @click="onPage('13')">
              {{ $t('隐私政策') }}
            </button>
            <button type="button" class="text-xs md:text-sm text-white/75 hover:text-white transition-colors"
              @click="onPage('14')">
              {{ $t('用户协议') }}
            </button>
          </div>
        </div>

        <div class="flex items-center gap-4 md:gap-5">
          <template v-for="(item, index) in socialList">
            <a v-if="item.link" :key="index" :href="item.link" target="_blank" rel="noopener noreferrer"
              :aria-label="item.name"
              class="w-8 h-8 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity">
              <img :src="item.icon" :alt="item.name" class="w-8 h-8 object-contain">
            </a>
            <span v-else-if="item.isWechat" :key="`wechat-${index}`" :aria-label="item.name"
              class="df-wechat-trigger w-8 h-8 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity">
              <img :src="item.icon" :alt="item.name" class="w-8 h-8 object-contain">
              <span v-if="item.qrcode" class="df-wechat-popup" role="tooltip">
                <img :src="item.qrcode" :alt="$t('微信二维码')" class="df-wechat-qrcode">
              </span>
            </span>
            <span v-else :key="`empty-${index}`" :aria-label="item.name"
              class="w-8 h-8 flex items-center justify-center opacity-80">
              <img :src="item.icon" :alt="item.name" class="w-8 h-8 object-contain">
            </span>
          </template>
        </div>
      </div>

      <div
        class="mt-8 md:mt-10 text-center text-[11px] md:text-xs text-white/45 leading-relaxed flex flex-wrap justify-center gap-x-3 gap-y-1">
        <span>Copyright © 2025 CHAOS科技有限公司</span>
        <span v-if="siteBeian">{{ siteBeian }}</span>
      </div>
    </div>

    <el-dialog custom-class="rounded-lg footer-dialog" :width="dialogWidth" title="" :visible.sync="pageShow" center
      :show-close="false" :append-to-body="true">
      <div class="max-h-[80vh] flex flex-col">
        <div class="text-center text-lg md:text-xl mb-6 text-black font-bold">{{ title }}</div>
        <div class="w-full p-4 md:p-6 bg-[#F8F8F8] rounded-2xl overflow-y-auto" v-html="pageContent"
          style="max-height: 50vh;"></div>
        <div class="mt-6 flex justify-center">
          <button class="h-10 md:h-12 px-12 md:px-20 bg-[#1A1A1A] text-white text-base rounded-lg active:opacity-80"
            @click="pageShow = false">
            {{ $t('关闭') }}
          </button>
        </div>
      </div>
    </el-dialog>
  </footer>
</template>

<script>
  import { getRegionList, pageIndex, postGetconf } from '@/api/index';
  import { postCarList, postTechnologyCategory } from '@/api/head';
  import { getLayoutData } from '@/utils/layoutDataCache';

  export default {
    name: 'FooterPage',
    data() {
      return {
        /** H5 手风琴：默认全部收起 */
        openAccIndexes: [],
        /** 页脚车型列使用的接口车型列表。 */
        carLinks: [],
        /** 页脚技术列使用的接口技术列表。 */
        technologyLinks: [],
        /** 页脚 Middle East Network 列使用的地区列表。 */
        regionLinks: [],
        staticLinkColumns: [
          {
            title: '零售与服务',
            links: [
              { name: '零售中心', path: '/retail' },
              { name: '服务中心', path: '/service' },
              { name: '预约试驾', action: 'testDrive' },
            ],
          },
          {
            title: '关于我们',
            links: [
              { name: '东风快讯', path: '/about/news' },
              { name: '关于我们', path: '/about' },
              { name: '联系我们', path: '/about/contact' },
            ],
          },
          {
            title: '其他工具',
            links: [
              { name: '车型参数对比', path: '/tools/compare' },
              { name: '金融试算器', path: '/tools/finance' },
            ],
          },
        ],
        socialList: [
          {
            name: 'Instagram',
            configKey: 'instagram_url',
            link: '',
            icon: require('../../../static/image/icon/footer/instagram.png'),
            iconWhite: require('../../../static/image/icon/footer/instagram-white.png'),
          },
          {
            name: 'Facebook',
            configKey: 'facebook_url',
            link: '',
            icon: require('../../../static/image/icon/footer/facebook.png'),
            iconWhite: require('../../../static/image/icon/footer/facebook-white.png'),
          },
          {
            name: 'WeChat',
            configKey: 'wechat_qrcode',
            link: '',
            /** 微信配置为二维码图片地址，不作为跳转链接使用。 */
            qrcode: '',
            isWechat: true,
            icon: require('../../../static/image/icon/footer/wechat.png'),
            iconWhite: require('../../../static/image/icon/footer/wechat-white.png'),
          },
          {
            name: 'Weibo',
            configKey: 'weibo_url',
            link: '',
            icon: require('../../../static/image/icon/footer/weibo.png'),
            iconWhite: require('../../../static/image/icon/footer/weibo-white.png'),
          },
        ],
        pageShow: false,
        pageContent: '',
        title: '',
        /** 页脚展示的站点备案信息。 */
        siteBeian: '',
        /** 页脚展示的客服电话。 */
        serviceMobile: '',
      };
    },
    computed: {
      /** 将所有车型分组整合为页脚的单一车型列。 */
      linkColumns() {
        return [
          // 车型、技术和地区链接名称由接口按当前语言返回。
          { title: '车型', links: this.carLinks, isApiData: true },
          { title: '技术', links: this.technologyLinks, isApiData: true },
          this.staticLinkColumns[0],
          { title: 'Middle East Network', links: this.regionLinks, isApiData: true },
          ...this.staticLinkColumns.slice(1),
        ];
      },
      dialogWidth() {
        if (this.$isMobile) return '92%';
        if (this.$isTablet) return '80%';
        return '60%';
      },
    },
    created() {
      this.fetchCarList();
      this.fetchTechnologyList();
      this.fetchRegionList();
      this.fetchContactLinks();
      this.fetchSiteConfig();
    },
    watch: {
      /** 语言切换后重新拉取已由接口本地化的页脚链接。 */
      '$i18n.locale'(language, previousLanguage) {
        if (language === previousLanguage) return;
        this.fetchCarList();
        this.fetchTechnologyList();
        this.fetchRegionList();
      },
    },
    methods: {
      /** 固定链接使用本地 i18n，接口链接直接展示接口返回值。 */
      formatLinkName(column, link) {
        if (!link) return '';
        return column && column.isApiData ? (link.name || '') : this.$t(link.name || '');
      },
      /** 获取页脚社交媒体链接与微信二维码配置。 */
      fetchContactLinks() {
        getLayoutData('site-config-contact', () => postGetconf({ module: 'contact' })).then((res) => {
          const contact = res && res.code === 1 && res.data && typeof res.data === 'object' ? res.data : {};
          this.serviceMobile = typeof contact.service_mobile === 'string' ? contact.service_mobile : '';
          this.socialList = this.socialList.map((item) => ({
            ...item,
            link: item.isWechat ? '' : (contact[item.configKey] || ''),
            qrcode: item.isWechat ? (contact[item.configKey] || '') : item.qrcode,
          }));
        }).catch(() => {
          // 配置请求失败时不跳转到无效的占位地址。
          this.serviceMobile = '';
          this.socialList = this.socialList.map((item) => ({ ...item, link: '', qrcode: '' }));
        });
      },
      /** 获取站点配置中的备案信息，供页脚展示。 */
      fetchSiteConfig() {
        getLayoutData('site-config-site', () => postGetconf({ module: 'site' })).then((res) => {
          this.siteBeian = res && res.code === 1 && res.data && typeof res.data.site_beian === 'string'
            ? res.data.site_beian
            : '';
        }).catch(() => {
          // 请求失败时不展示静态备案信息，避免与后台配置不一致。
          this.siteBeian = '';
        });
      },
      /** 获取并合并所有车型，供页脚车型列完整渲染。 */
      fetchCarList() {
        getLayoutData('car-list', postCarList).then((res) => {
          if (res.code !== 1 || !res.data || typeof res.data !== 'object') {
            this.carLinks = [];
            return;
          }

          this.carLinks = Object.keys(res.data).reduce((links, key) => {
            const group = res.data[key];
            if (!group || !Array.isArray(group.list)) return links;

            group.list.forEach((car) => {
              if (!car || !car.id || !car.name) return;
              links.push({
                name: car.name,
                path: `/car/${car.id}`,
              });
            });
            return links;
          }, []);
        }).catch(() => {
          // 请求失败时清空列表，避免展示过期的静态车型数据。
          this.carLinks = [];
        });
      },
      /** 获取并合并各分类技术，供页脚技术列完整渲染。 */
      fetchTechnologyList() {
        const categories = [0, 1];

        Promise.all(categories.map((type) => getLayoutData(
          `technology-category-${type}`,
          () => postTechnologyCategory({ type }),
        )
          .then((res) => (res.code === 1 && Array.isArray(res.data) ? res.data : []))
          .catch(() => [])))
          .then((groups) => {
            this.technologyLinks = groups.reduce((links, group) => {
              group.forEach((technology) => {
                if (!technology || !technology.id || !technology.name) return;
                links.push({
                  name: technology.name,
                  path: {
                    path: '/technology',
                    query: { category_id: technology.id },
                  },
                });
              });
              return links;
            }, []);
          });
      },
      /** 获取顶级地区数据，供页脚跳转预约试驾并预选国家。 */
      fetchRegionList() {
        getLayoutData('top-region-list', () => getRegionList({ parent_id: 0 })).then((res) => {
          if (res.code !== 1 || !Array.isArray(res.data)) {
            this.regionLinks = [];
            return;
          }

          this.regionLinks = res.data
            .filter((region) => region && region.id && (region.name || region.name_zh))
            .map((region) => ({
              name: region.name || region.name_zh,
              path: {
                path: '/testdrive',
                query: { country_code: region.id },
              },
            }));
        }).catch(() => {
          // 请求失败时清空列表，避免展示过期的静态地区数据。
          this.regionLinks = [];
        });
      },
      isAccOpen(index) {
        return this.openAccIndexes.indexOf(index) !== -1;
      },
      toggleAcc(index) {
        const i = this.openAccIndexes.indexOf(index);
        if (i === -1) {
          this.openAccIndexes.push(index);
        } else {
          this.openAccIndexes.splice(i, 1);
        }
      },
      onLinkClick(link) {
        if (link.action === 'testDrive') {
          this.$router.push({ path: '/testdrive' }).catch(() => { });
          return;
        }
        if (link.id) this.onPage(link.id);
      },
      onPage(id) {
        pageIndex({ type: id }).then((res) => {
          const titleMap = {
            '13': '隐私政策',
            '14': '用户协议',
          };
          this.title = titleMap[id] || '';
          this.pageContent = res.data.content;
          this.pageShow = true;
        });
      },
    },
  };
</script>

<style scoped>
  .df-footer-link {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline;
  }

  /* 微信图标悬停时展示后台配置的二维码图片。 */
  .df-wechat-trigger {
    position: relative;
    cursor: pointer;
  }

  .df-wechat-popup {
    position: absolute;
    z-index: 20;
    bottom: calc(100% + 12px);
    left: 50%;
    width: 148px;
    padding: 8px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) translateY(6px);
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .df-wechat-trigger:hover .df-wechat-popup {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  .df-wechat-qrcode {
    display: block;
    width: 100%;
    height: auto;
  }

  ::v-deep .footer-dialog {
    border-radius: 1rem !important;
  }
</style>
